<template>
  <div class="mathjax-container">
    <!-- 使用v-html渲染处理后的内容 -->
    <div ref="mathContent" class="math-content" v-html="processedContent"></div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <span>正在加载公式渲染器...</span>
    </div>

    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
  export default {
    name: 'MathJaxFormula',

    props: {
      // 内容字符串，可以包含LaTeX公式和普通文本
      content: {
        type: String,
        required: true
      },
      // 是否自动换行
      wrap: {
        type: Boolean,
        default: true
      },
      // 是否使用行内模式（默认块级）
      inline: {
        type: Boolean,
        default: true
      },
      // 是否延迟渲染（用于性能优化）
      lazy: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        loading: true,
        error: null,
        mathjaxLoaded: false,
        processedContent: '',
        observer: null
      }
    },

    watch: {
      content: {
        immediate: true,
        handler() {
          this.processAndRender()
        }
      },
      inline() {
        this.processAndRender()
      }
    },

    mounted() {
      this.loadMathJax()

      // 创建MutationObserver监听DOM变化（用于动态内容）
      if (typeof MutationObserver !== 'undefined') {
        this.observer = new MutationObserver(() => {
          if (this.mathjaxLoaded) {
            this.renderMath()
          }
        })

        this.observer.observe(this.$refs.mathContent, {
          childList: true,
          subtree: true
        })
      }
    },

    beforeDestroy() {
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    methods: {
      // 加载MathJax库
      loadMathJax() {
        if (window.MathJax) {
          this.mathjaxLoaded = true
          this.loading = false
          this.processAndRender()
          return
        }

        // 动态加载MathJax
        const script = document.createElement('script')
        script.src =
          'https://onlinelibres1.noahedu.com/advertising/279/html/dist/js/mathjax/MathJax.js?config=TeX-MML-AM_CHTML'
        // npm i mathjax@2.7.7
        // const isDev = process.env.NODE_ENV === 'development';
        // const mathjaxPath = isDev
        //   ? '/node_modules/mathjax/MathJax.js?config=TeX-MML-AM_CHTML'
        //   : './mathjax/MathJax.js?config=TeX-MML-AM_CHTML';
        // script.src = mathjaxPath;

        script.async = true

        script.onload = () => {
          window.MathJax.Hub.Config({
            tex2jax: {
              inlineMath: [
                ['$', '$'],
                ['\\(', '\\)']
              ],
              displayMath: [
                ['$$', '$$'],
                ['\\[', '\\]']
              ],
              processEscapes: true,
              processEnvironments: true
            },
            'HTML-CSS': {
              availableFonts: ['TeX'],
              linebreaks: { automatic: this.wrap }
            },
            displayAlign: 'center',
            displayIndent: '0',
            extensions: ['AMSmath.js', 'AMSsymbols.js', 'enclose.js']
          })

          this.mathjaxLoaded = true
          this.loading = false
          this.processAndRender()
        }

        script.onerror = () => {
          this.error = '无法加载MathJax库，请检查网络连接'
          this.loading = false
        }

        document.head.appendChild(script)
      },

      // 处理内容并渲染
      processAndRender() {
        if (!this.content) {
          this.processedContent = ''
          return
        }

        // 预处理内容：将LaTeX公式用适当的分隔符包裹
        let processed = this.content

        // 如果不是懒加载，立即处理
        if (!this.lazy) {
          this.processedContent = this.wrapFormulas(processed)

          // 等待DOM更新后渲染MathJax
          this.$nextTick(() => {
            if (this.mathjaxLoaded) {
              this.renderMath()
            }
          })
        } else {
          // 懒加载模式：只显示纯文本
          this.processedContent = this.stripLatex(processed)
        }
      },

      // 包装LaTeX公式
      wrapFormulas(content) {
        if (this.inline) {
          // 行内模式：使用 $...$ 包裹
          return content.replace(/\\\(.*?\\\)/g, (match) => {
            return `<span class="math-inline">${match}</span>`
          })
        } else {
          // 块级模式：使用 $$...$$ 包裹
          return content.replace(/\\\[.*?\\\]/g, (match) => {
            return `<div class="math-block">${match}</div>`
          })
        }
      },

      // 移除LaTeX标记（用于懒加载模式）
      stripLatex(content) {
        return content
          .replace(/\\\(/g, '')
          .replace(/\\\)/g, '')
          .replace(/\\\[/g, '')
          .replace(/\\\]/g, '')
          .replace(/\$/g, '')
      },

      // 渲染MathJax
      renderMath() {
        if (!window.MathJax || !this.mathjaxLoaded) {
          return
        }

        try {
          window.MathJax.Hub.Queue([
            'Typeset',
            window.MathJax.Hub,
            this.$refs.mathContent
          ])
        } catch (err) {
          console.error('MathJax渲染错误:', err)
          this.error = `公式渲染失败: ${err.message}`
        }
      },

      // 手动触发渲染（可用于外部调用）
      refresh() {
        if (this.mathjaxLoaded) {
          this.renderMath()
        }
      }
    }
  }
</script>

<style scoped>
  .mathjax-container {
    position: relative;
    width: 100%;
  }

  .math-content {
    line-height: 1.6;
    font-size: 16px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .math-inline {
    display: inline-block;
    margin: 0 2px;
  }

  .math-block {
    display: block;
    margin: 1em 0;
    padding: 0.5em;
    background-color: #f9f9f9;
    border-radius: 4px;
    overflow-x: auto;
  }

  .loading {
    padding: 10px;
    text-align: center;
    color: #666;
    font-style: italic;
  }

  .error-message {
    padding: 10px;
    background-color: #ffe6e6;
    border: 1px solid #ffcccc;
    border-radius: 4px;
    color: #cc0000;
    margin-top: 10px;
  }

  /* 覆盖MathJax默认样式 */
  :deep(.MathJax) {
    outline: none;
  }

  :deep(.MathJax_Display) {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5em 0;
  }
</style>
